import { Button, Card, Input, useToast } from '@pharmaconnect/ui/web'
import { useEffect, useState } from 'react'

import { challengeAndVerify, listFactors } from '../services/mfa'
import { useAuthStore } from '../stores/authStore'

/**
 * Affiché après login email/password si l'utilisateur a un facteur TOTP
 * vérifié et que la session est en aal1. À la vérification, la session
 * passe en aal2 et le hook applySession met à jour `mfaRequired`.
 */
export function MfaChallenge() {
  const toast = useToast()
  const refreshAal = useAuthStore((s) => s.refreshAal)
  const logout = useAuthStore((s) => s.logout)
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [factorId, setFactorId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const factors = await listFactors()
        const totp = factors.find((f) => f.factor_type === 'totp' && f.status === 'verified')
        if (!totp) {
          setError('Aucun facteur TOTP vérifié. Contactez votre administrateur.')
          return
        }
        setFactorId(totp.id)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur factorList')
      }
    })()
  }, [])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!factorId) return
    if (!/^\d{6}$/.test(code)) {
      setError('Code à 6 chiffres requis')
      return
    }
    setError(null)
    setLoading(true)
    try {
      await challengeAndVerify(factorId, code)
      await refreshAal()
      toast.success('Authentification renforcée OK')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code invalide')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-pharmacist-bg px-4">
      <Card className="w-full max-w-sm" noPadding>
        <div className="border-b border-white/10 p-6">
          <h1 className="text-xl font-bold text-pharmacist">Authentification à 2 facteurs</h1>
          <p className="mt-1 text-xs text-neutral-400">
            Saisissez le code à 6 chiffres affiché par votre application TOTP (Google Authenticator,
            Authy, 1Password…).
          </p>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col gap-4 p-6" data-testid="mfa-form">
          <Input
            label="Code à 6 chiffres"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            pattern="[0-9]{6}"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
            autoFocus
            data-testid="mfa-code"
            {...(error ? { error } : {})}
          />
          <Button
            type="submit"
            loading={loading}
            disabled={code.length !== 6 || !factorId}
            fullWidth
            data-testid="mfa-submit"
          >
            Vérifier
          </Button>
          <button
            type="button"
            onClick={() => {
              void logout()
            }}
            className="text-center text-xs text-neutral-400 hover:text-pharmacist hover:underline"
            data-testid="mfa-logout"
          >
            Déconnexion
          </button>
        </form>
      </Card>
    </main>
  )
}
